export interface User {
  id: string;
  email: string;
  name: string;
  /** ISO timestamp, or null if the pilot terms have not been accepted yet. */
  consented_at: string | null;
  /** May edit the templates every user shares. The server checks again. */
  is_admin?: boolean;
}

export interface Citation {
  chunk_id: string;
  family_id: string;
  doc_id: string;
  /** Act title, e.g. "Codul muncii al Republicii Moldova". */
  title: string;
  /** Human-readable location, e.g. "Articolul 86, alin. (1)". */
  label: string;
  /**
   * Where the passage sits in the act, e.g. "/capitolul-vi/articolul-24".
   * The document panel scrolls to the node with this exact path. Null on
   * answers stored before the panel existed - those open at the top.
   */
  path: string | null;
  effective_from: string | null;
  effective_to: string | null;
  /** Whether this version is the one in force today. Laws only. */
  is_current: boolean;
  /**
   * The passage cited. For a decision: the anonymised excerpt the answer
   * was given, or "" when the model only read the decision whole.
   */
  snippet: string;
  /**
   * "decision" for a CSJ judgment; absent on answers stored before
   * decisions could be cited, which are all laws. A decision opens its own
   * panel - the excerpt and the court's copy - not the act viewer, and is
   * labelled court practice, never "în vigoare".
   */
  kind?: "law" | "decision";
  /** Decisions only: "1ra-1047/2024". */
  case_number?: string | null;
  /** Decisions only: ISO date of the decision. */
  decided_on?: string | null;
  /** Decisions only: the decision on the court's own site. */
  url?: string | null;
  /**
   * Decisions only: which part of the judgment the excerpt starts in.
   * "facts" is the courts below retold - not the CSJ - and the panel warns.
   */
  part?: "intro" | "facts" | "law" | "reasoning" | "ruling" | "guidance" | "unknown" | null;
}

/** One provision of an act, as returned by GET /documents/{doc_id}. */
export interface DocumentNode {
  node_id: string;
  /** "article", "chapter", "paragraph", "point", "annex", "text", … */
  node_type: string;
  number: string | null;
  heading: string | null;
  path: string;
  depth: number;
  /** The node's own text, excluding descendants. Empty for headings. */
  text: string;
}

export interface DocumentDetail {
  doc_id: string;
  family_id: string;
  title: string;
  act_type: string | null;
  act_number: string | null;
  act_number_full: string | null;
  /** The masthead legis.md prints above every act. */
  issuing_authority: string | null;
  adoption_date: string | null;
  publication_source: string | null;
  effective_from: string | null;
  effective_to: string | null;
  is_current: boolean;
  /** The act has been abrogated; the panel says so loudly. */
  repealed: boolean;
  /**
   * The act's own markup from the archived legis.md page, sanitised server
   * side. Null when the archive has no page for it; the panel then renders
   * from `nodes`, which is complete but plain.
   */
  html: string | null;
  nodes: DocumentNode[];
}

/**
 * Three axes rather than a thumbs up/down, because they are three different
 * failure modes with three different fixes: the wrong act is a retrieval
 * problem, the wrong version is a currency problem, and a correct citation
 * that does not answer the question is a generation problem.
 */
export interface Rating {
  law_correct: boolean | null;
  version_correct: boolean | null;
  answers_question: boolean | null;
  comment: string;
}

export type Verdict = boolean | null;

/**
 * A step as it arrives over the stream, while the agent is still working.
 *
 * Each tool reports twice under one `id`: `running` when it starts, carrying
 * what is being searched for, then `done` with what came back. The caller
 * replaces by `id` rather than appending, so the list shows three steps that
 * progress rather than six that accumulate.
 */
export interface StreamStep {
  id: number;
  tool: string;
  label: string;
  detail?: string | null;
  status: "running" | "done";
}

/** One tool call the agent made, already phrased in Romanian by the API. */
export interface Step {
  step?: number | null;
  tool: string;
  /** e.g. 'Caut în legislație: „concediere în perioada concediului medical”' */
  label: string;
  /** e.g. '6 pasaje, 3 noi' — omitted when there is nothing to add. */
  detail?: string | null;
}

/**
 * A file the user uploaded to ask about.
 *
 * `pages` is the meter. For a `.docx` it is derived rather than counted —
 * pagination is a property of rendering, not of the file — which is what
 * `page_basis` records, and why the interface marks that number approximate
 * instead of quoting it as though it had been counted.
 */
export interface Attachment {
  id: string;
  filename: string;
  /** "pdf" | "docx" | "image". */
  kind: string;
  /** "pending" | "ready" | "failed" | "deleted". */
  status: string;
  size_bytes: number | null;
  pages: number | null;
  page_basis: string | null;
  /** Present when status is "failed": the sentence to show the user. */
  failure: string | null;
}

/** Where to send the bytes, from POST /uploads. */
export interface UploadTicket {
  attachment_id: string;
  /** "s3" in production — the browser posts straight to the bucket. */
  mode: string;
  url: string;
  fields: Record<string, string>;
  max_bytes: number;
}

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
  /** Files attached to this turn. Only ever on a user message. */
  attachments?: Attachment[];
  /**
   * What the agent did to reach the answer. Absent on user messages and on
   * answers given before the trace was surfaced.
   */
  steps?: Step[];
  /**
   * Documents the agent produced while answering. Only ever on an assistant
   * message, and persisted, so reopening the thread still shows what the
   * answer made rather than only describing it.
   */
  drafts?: DraftRef[];
  rating?: Rating | null;
  created_at: string;
  /** Set when generation failed; content then holds the user-facing reason. */
  error?: boolean;
}

export interface ContextInfo {
  used_tokens: number;
  max_tokens: number;
  /** Set once the thread is expensive enough that a fresh one is better. */
  should_start_new: boolean;
}

export interface AskResponse {
  message: Message;
  conversation_id: string;
  context: ContextInfo;
}

export interface Conversation {
  id: string;
  title: string;
  created_at: string;
  message_count: number;
  last_at: string;
}

export interface ConversationDetail {
  id: string;
  title: string;
  created_at: string;
  messages: Message[];
  context: ContextInfo;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/* ----------------------------------------------------------- drafting
 *
 * `DocumentDetail` above is an act from the corpus. Everything below is a
 * document the user is *writing*, named Draft throughout so the two cannot
 * be confused.
 */

/** The provision that requires a field. */
export interface SlotSource {
  family_id: string;
  path: string;
  /** Human-readable, e.g. "art. 49 alin. (1) lit. a)". */
  note: string;
  /** The consolidation in force now, for opening the document panel. */
  doc_id: string | null;
  /** False when the path no longer resolves — renumbered or repealed. */
  present: boolean;
  /** The act was consolidated after this template was written. Advisory. */
  possibly_amended: boolean;
}

export interface Slot {
  key: string;
  label: string;
  /** "text" | "date" | "amount" | "longtext" — picks the input to render. */
  kind: string;
  required: boolean;
  help_text: string;
  source: SlotSource | null;
  /** The form section: where it was placed, else the heading of the first
   *  clause that uses it. "" for an added field not placed yet. */
  section: string;
  /** Added in this draft by typing its placeholder into a clause. */
  added?: boolean;
}

export interface TemplateSummary {
  slug: string;
  title: string;
  summary: string;
  doc_kind: string;
  version: number;
  slot_count: number;
  /** Fields that cite a provision. The difference from slot_count is the
   *  difference between this and a template website. */
  sourced_count: number;
  /** The act most fields rest on, e.g. "Codul muncii, art. 49". */
  basis_label: string | null;
  /** The user's own copy ("Modelele mele"), not the shared library. */
  mine?: boolean;
  /** For a copy: the title of the template it was copied from. */
  copied_from?: string | null;
}

export interface TemplateDetail extends TemplateSummary {
  slots: Slot[];
}

/** A run of clause text: the template's wording, or one field. */
export interface Segment {
  text: string;
  /** The field this run came from; null for template wording. */
  slot: string | null;
  /** An empty field. `text` is then "", and the preview draws the gap. */
  missing: boolean;
}

export interface Clause {
  key: string;
  heading: string;
  text: string;
  /** The lawyer replaced the template's wording. */
  edited: boolean;
  /** Field keys still unfilled here, so the gap is marked where it shows. */
  missing: string[];
  segments: Segment[];
}

/** A required field with no value, and the provision requiring it. */
export interface Gap {
  key: string;
  label: string;
  note: string;
  family_id: string | null;
  path: string | null;
  doc_id: string | null;
}

/**
 * A draft as an answer points at it.
 *
 * Narrower than DraftSummary on purpose: no `updated_at`, because that is a
 * property of the document now and would make a stored answer appear to
 * change. `missing_count` is recounted server-side on every load, so the
 * card beside a six-week-old answer still says how much is left to fill.
 */
export interface DraftRef {
  id: string;
  title: string;
  template_slug: string;
  template_title: string;
  missing_count: number;
}

export interface DraftSummary {
  id: string;
  title: string;
  template_slug: string;
  template_title: string;
  missing_count: number;
  created_at: string;
  updated_at: string;
}

export interface DraftDetail {
  id: string;
  title: string;
  template_slug: string;
  template_title: string;
  template_version: number;
  slots: Slot[];
  values: Record<string, string>;
  clauses: Clause[];
  /** Every form section in document order, including ones with no field
   *  yet - an added field can be placed in any of them. */
  sections: string[];
  /** The document as the rich-text editor holds it (TipTap JSON): saved by
   *  the lawyer, or generated from the template until they save. */
  content: Record<string, unknown>;
  /** True once the lawyer has saved the document in the editor. */
  edited: boolean;
  gaps: Gap[];
  /** Fields the law requires whose placeholder was deleted from the text:
   *  not in the form, not counted, but named. */
  removed?: Gap[];
  created_at: string;
  updated_at: string;
}

/** One clause of a template, as the editor works on it. */
export interface TemplateClause {
  /** Empty for a clause just added; the server names it on save. */
  key: string;
  heading: string;
  /** Carries {{placeholder}} fields. */
  text: string;
}

/** A template's newest version, for the template editor. */
export interface TemplateSource {
  slug: string;
  title: string;
  summary: string;
  doc_kind: string;
  version: number;
  changelog: string;
  published_at: string | null;
  clauses: TemplateClause[];
  slots: Slot[];
  sections: string[];
  /** The user's own copy. */
  mine: boolean;
  /** May save it: the owner for a copy, an admin for the library. */
  can_edit: boolean;
}

/** What the editor says about one field. The fields themselves follow the
 *  placeholders in the text. */
export interface TemplateFieldIn {
  label: string;
  kind: string;
  required: boolean;
  help_text: string;
  /** Where it was placed with "+"; "" leaves the default. */
  section: string;
}

export interface TemplateSave {
  /** The version the editor started from; a newer one makes the save fail. */
  base_version: number;
  title: string;
  summary: string;
  clauses: TemplateClause[];
  fields: Record<string, TemplateFieldIn>;
  changelog: string;
}

/**
 * Data-protection compliance. Mirrors the schemas in backend/app/schemas.py.
 */

export interface Question {
  field: string;
  kind: "bool" | "number" | "text" | "choice";
  question: string;
  help: string;
  options: string[];
  /** null is unanswered, which is not the same as false. */
  answer: boolean | number | string | null;
  /** Obligations a Da / Nu would add, and what the given answer added. */
  impact: { yes: number; no: number; adds: number } | null;
}

/** The three conclusions the Obligații tab leads with. null = not known yet. */
export interface Verdicts {
  records_exempt: boolean | null;
  /** Profile fields that cancel the art. 30(5) exemption. */
  records_triggers: string[];
  dpo_required: boolean | null;
  dpo_triggers: string[];
  consent_reuse: boolean | null;
}

export interface Obligation {
  code: string;
  title: string;
  summary: string;
  kind: "standing" | "event" | "determination";
  article: string;
  family_id: string;
  path: string;
  fine_tier: string | null;
  evidence_expected: string;
  state: "open" | "in_progress" | "done" | "not_applicable";
  evidence_ref: string;
  determination: string;
  needs_review_since: string | null;
  review_reason: string;
  /** Non-empty only in `unknown`: the questions that would settle it. */
  missing_answers: string[];
}

export interface Assessment {
  owed: Obligation[];
  exempt: Obligation[];
  unknown: Obligation[];
  answered: number;
  questions_total: number;
  /** How far through the questions, never a compliance score. */
  profile_complete: boolean;
  reviewed_by_lawyer: boolean;
  verdicts: Verdicts | null;
}

export interface Activity {
  id: string;
  name: string;
  purpose: string;
  lawful_basis_art: string;
  subject_categories: string[];
  data_categories: string[];
  recipient_categories: string[];
  transfers_to: string[];
  retention_rule: string;
  security_measures: string;
  confirmed: boolean;
  updated_at: string | null;
}

export interface ActivityGap {
  id: string;
  name: string;
  confirmed: boolean;
  missing: { field: string; required: boolean; article: string }[];
}

export interface Contact {
  id: string;
  role: "operator" | "operator_asociat" | "reprezentant" | "dpo";
  name: string;
  email: string;
  phone: string;
}

/** One wording of a provision, from GET /history/{family_id}. */
export interface HistoryEntry {
  path: string | null;
  number: string | null;
  heading: string | null;
  text: string;
  effective_from: string | null;
  effective_to: string | null;
  repealed: boolean;
}

/* ------------------------------------------------------------- memory */

export type FactCategory = "company" | "people" | "preferences";

/**
 * One remembered fact. `id` is "profile:<field>" for a fact read live from
 * the compliance profile - editing it edits the profile.
 */
export interface MemoryFact {
  id: string;
  category: FactCategory;
  key: string;
  label: string;
  value: string;
  source: "user" | "profile" | "inferred" | "document";
  source_ref: string | null;
  source_note: string;
  file_name: string | null;
  /** A proposal is shown but not used in answers until confirmed. */
  confirmed: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface Memory {
  enabled: boolean;
  facts: MemoryFact[];
}
